/**
 * OnboardingFormModal Component
 *
 * Modal form for an organisation admin to onboard a new practitioner
 * (or other role) into the project. Calls the backend
 * `POST /admin/onboard-user` endpoint which creates all the required
 * FHIR resources (User, Practitioner, PractitionerRole, AccessPolicy,
 * UserConfiguration, ProjectMembership) in a single transaction.
 *
 * The form uses react-hook-form + zod for validation and follows the
 * same modal styling conventions as PatientFormContent.
 */

import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Modal } from '@/components/base';
import { onboardingApi } from '@/lib/api/onboarding';
import { createComponentLogger } from '@/lib/utils/logger';
import type { OnboardingFormValues, RoleOption } from '@/lib/types/onboarding';
import { DEFAULT_ROLE_OPTIONS } from '@/lib/types/onboarding';
import { useSpecialties } from '@/lib/hooks/useSpecialties';
import { AdditionalSpecialtiesInput } from '@/components/forms/AdditionalSpecialtiesInput';
import { SpecialtyPicker } from '@/components/forms/SpecialtySearch';
import { INPUT_CLASS, LABEL_CLASS, ERROR_CLASS } from '@/components/forms/formStyles';

const logger = createComponentLogger('OnboardingFormModal');

/**
 * Onboarding form validation schema.
 *
 * Password rules follow the backend requirement of a minimum of 8
 * characters. We additionally require at least one letter and one
 * number for stronger defaults.
 */
const onboardingSchema = z
  .object({
    firstName: z
      .string()
      .min(2, 'First name must be at least 2 characters')
      .max(50, 'First name must not exceed 50 characters')
      .refine((val) => /^[a-zA-Z\s]+$/.test(val), {
        message: 'First name can only contain letters and spaces',
      }),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(50, 'Last name must not exceed 50 characters')
      .refine((val) => /^[a-zA-Z\s]+$/.test(val), {
        message: 'Last name can only contain letters and spaces',
      }),
    email: z.string().min(1, 'Email is required').email('Please enter a valid email address'),
    phone: z
      .string()
      .optional()
      .refine((val) => {
        if (!val) return true;
        return /^\d{10}$/.test(val);
      }, 'Phone number must be exactly 10 digits'),
    role: z.string().min(1, 'Please select a role'),
    specialty: z.string().optional(),
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .refine((val) => /[a-zA-Z]/.test(val), {
        message: 'Password must contain at least one letter',
      })
      .refine((val) => /\d/.test(val), {
        message: 'Password must contain at least one number',
      }),
    confirmPassword: z.string().min(1, 'Please confirm the password'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((data) => data.role !== 'practitioner' || !!data.specialty, {
    message: 'Specialty is required for practitioners',
    path: ['specialty'],
  });

export interface OnboardingFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful onboarding (e.g. to refetch the list) */
  onSuccess?: () => void;
}

export function OnboardingFormModal({ isOpen, onClose, onSuccess }: OnboardingFormModalProps) {
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  // Fetch available roles from the backend (falls back to defaults)
  const { data: roles = DEFAULT_ROLE_OPTIONS } = useQuery({
    queryKey: ['onboarding-roles'],
    queryFn: () => onboardingApi.fetchRoles(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema) as never,
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'practitioner',
      specialty: '',
      password: '',
      confirmPassword: '',
    },
  });

  const selectedRole = watch('role');
  const isPractitionerRole = selectedRole === 'practitioner';
  const {
    data: specialties = [],
    isLoading: isLoadingSpecialties,
    error: specialtiesError,
  } = useSpecialties();
  const specialtiesStatus = isLoadingSpecialties
    ? 'Loading specialties…'
    : specialtiesError
      ? specialtiesError.message
      : undefined;
  const [additionalSpecialties, setAdditionalSpecialties] = React.useState<string[]>([]);

  // Onboarding mutation
  const onboardMutation = useMutation({
    mutationFn: onboardingApi.onboardUser,
    onSuccess: (data) => {
      logger.log('Onboarding successful:', data);
      toast.success(
        `Practitioner onboarded successfully. ${data.message ? data.message : ''}`.trim()
      );
      // Invalidate the practitioners list so the new user appears
      queryClient.invalidateQueries({ queryKey: ['practitioners'] });
      queryClient.invalidateQueries({ queryKey: ['onboarded-users'] });
      reset();
      setAdditionalSpecialties([]);
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to onboard practitioner';
      logger.error('Onboarding error:', message);
      toast.error(message);
    },
  });

  const onSubmit = handleSubmit((values) => {
    onboardMutation.mutate({
      email: values.email.trim(),
      password: values.password,
      role: values.role,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      ...(isPractitionerRole && values.specialty
        ? { specialty: [values.specialty, ...additionalSpecialties] }
        : {}),
      ...(values.phone ? { phone: values.phone } : {}),
    });
  });

  const handleClose = () => {
    if (isSubmitting || onboardMutation.isPending) return;
    reset();
    setAdditionalSpecialties([]);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      size="lg"
      hideCloseButton={false}
      disableOutsideClick={true}
    >
      <div
        className="max-h-[80vh] overflow-y-auto pr-2"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db #f3f4f6' }}
      >
        {/* Header */}
        <div className="mb-3 text-center">
          <h2 className="font-hanken text-lg font-medium leading-overview-title tracking-normal text-gray-700">
            Onboard Practitioner
          </h2>
          <p className="mx-auto mt-1 w-3/4 font-hanken text-sm font-normal leading-label-2 tracking-label-2 text-gray-700">
            Create a new user account with the appropriate role, access policy and menu
            configuration.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-2.5">
          {/* First Name + Last Name */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                First Name
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <input
                type="text"
                placeholder="Enter first name"
                className={INPUT_CLASS}
                {...register('firstName')}
                disabled={isSubmitting}
              />
              {errors.firstName && <p className={ERROR_CLASS}>{errors.firstName.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Last Name
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <input
                type="text"
                placeholder="Enter last name"
                className={INPUT_CLASS}
                {...register('lastName')}
                disabled={isSubmitting}
              />
              {errors.lastName && <p className={ERROR_CLASS}>{errors.lastName.message}</p>}
            </div>
          </div>

          {/* Email + Phone */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                Email
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <input
                type="email"
                placeholder="user@hospital.com"
                className={INPUT_CLASS}
                {...register('email')}
                disabled={isSubmitting}
              />
              {errors.email && <p className={ERROR_CLASS}>{errors.email.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>Phone (optional)</label>
              <input
                type="tel"
                placeholder="10-digit phone"
                className={INPUT_CLASS}
                {...register('phone')}
                disabled={isSubmitting}
                maxLength={10}
              />
              {errors.phone && <p className={ERROR_CLASS}>{errors.phone.message}</p>}
            </div>
          </div>

          {/* Role + Specialty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                Role
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <select
                className={`${INPUT_CLASS} appearance-none`}
                {...register('role')}
                disabled={isSubmitting}
              >
                {roles.map((role: RoleOption) => (
                  <option key={role.code} value={role.code}>
                    {role.display}
                  </option>
                ))}
              </select>
              {errors.role && <p className={ERROR_CLASS}>{errors.role.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Specialty
                {isPractitionerRole ? (
                  <span className="ml-0.5 text-destructive">*</span>
                ) : (
                  <span className="ml-1 text-xs text-gray-400">(practitioners only)</span>
                )}
              </label>
              <Controller
                control={control}
                name="specialty"
                render={({ field }) => (
                  <SpecialtyPicker
                    specialties={specialties}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isSubmitting || !isPractitionerRole}
                    invalid={!!errors.specialty}
                    statusMessage={specialtiesStatus}
                  />
                )}
              />
              {errors.specialty && <p className={ERROR_CLASS}>{errors.specialty.message}</p>}
            </div>
          </div>

          {isPractitionerRole && (
            <div>
              <label className={LABEL_CLASS}>
                Additional specialties
                <span className="ml-1 text-xs text-gray-400">(optional)</span>
              </label>
              <AdditionalSpecialtiesInput
                value={additionalSpecialties}
                onChange={setAdditionalSpecialties}
                specialties={specialties}
                disabled={isSubmitting}
                statusMessage={specialtiesStatus}
              />
            </div>
          )}

          {/* Password + Confirm Password */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={LABEL_CLASS}>
                Temporary Password
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 8 characters"
                  className={INPUT_CLASS}
                  {...register('password')}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && <p className={ERROR_CLASS}>{errors.password.message}</p>}
            </div>
            <div>
              <label className={LABEL_CLASS}>
                Confirm Password
                <span className="ml-1 text-destructive" aria-label="required">
                  *
                </span>
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Re-enter password"
                  className={INPUT_CLASS}
                  {...register('confirmPassword')}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  tabIndex={-1}
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className={ERROR_CLASS}>{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

          {/* Helper note */}
          <p className="rounded-lg bg-[#f8f9fb] px-3 py-2 font-hanken text-xs text-gray-600">
            The user will receive access to the project based on the selected role. They can sign in
            immediately using the email and temporary password above.
          </p>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="h-9 rounded-md border border-gray-300 bg-white px-6 font-hanken text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex h-9 items-center gap-2 rounded-md bg-[#061a61] px-6 font-hanken text-sm font-medium text-white transition-colors hover:bg-[#0a2480] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting ? 'Onboarding...' : 'Onboard Practitioner'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default OnboardingFormModal;
